/* PDF generation for vacancy texts and chat transcripts */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { norm } from './textNormalization';
import { detectActualVacancyText, cleanVacancyText, parseVacancyToSections } from './vacancyUtils';

/**
 * Generate a professionally styled vacancy PDF.
 * Detects the vacancy text from messages, cleans it, and renders a
 * branded multi-section PDF with header, body sections, and footer.
 *
 * @param {Array} messages - Chat message array ({ who, type, text })
 * @returns {boolean} true if PDF was generated, false if no vacancy found
 */
export const generateVacancyPDF = (messages) => {
  const rawVacature = detectActualVacancyText(messages);
  const cleanedVacature = cleanVacancyText(rawVacature);

  if (!cleanedVacature || cleanedVacature.length < 100) {
    return false;
  }

  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    putOnlyUsedFonts: true,
    compress: true
  });

  // Extract job title voor header
  const firstLine = cleanedVacature.split('\n')[0] || '';
  let jobTitle = firstLine.length > 60 ? firstLine.substring(0, 57) + '...' : firstLine;
  jobTitle = jobTitle.replace(/\*\*/g, '').replace(/^Functietitel:\s*/i, '').trim();

  // === HEADER DESIGN ===

  // Hoofdgradient
  pdf.setFillColor(78, 84, 200);
  pdf.rect(0, 0, 210, 55, 'F');

  // Overlay gradient
  pdf.setFillColor(143, 148, 251);
  pdf.rect(0, 0, 210, 45, 'F');

  // Accent kleur - rechts
  pdf.setFillColor(102, 217, 232);
  pdf.rect(130, 0, 80, 55, 'F');

  // Subtiele textuur lijnen
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(0.1);
  pdf.setGState(new pdf.GState({ opacity: 0.1 }));
  for (let i = 0; i < 210; i += 15) {
    pdf.line(i, 0, i + 8, 55);
  }
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Logo/initialen
  pdf.setFillColor(255, 255, 255);
  pdf.circle(32, 27, 12, 'F');

  // Logo schaduw effect
  pdf.setFillColor(0, 0, 0);
  pdf.setGState(new pdf.GState({ opacity: 0.1 }));
  pdf.circle(32.5, 27.5, 12, 'F');
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Logo tekst
  pdf.setTextColor(78, 84, 200);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text('JG', 27.5, 31.5);

  // Hoofdtitel
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(26);
  pdf.text('VACATURETEKST', 52, 24);

  // Functie titel
  if (jobTitle) {
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'normal');
    const titleLines = pdf.splitTextToSize(jobTitle.toUpperCase(), 150);
    if (titleLines.length > 1) {
      pdf.text(titleLines[0], 52, 32);
      if (titleLines[1]) pdf.text(titleLines[1], 52, 37);
    } else {
      pdf.text(jobTitle.toUpperCase(), 52, 32);
    }
  }

  // Datum en branding
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'italic');
  const today = new Date();
  const dateStr = today.toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  pdf.text(`Gegenereerd op ${dateStr}`, 52, jobTitle ? 44 : 39);
  pdf.text('Jesse - Digital Recruiter', 52, jobTitle ? 49 : 44);

  // === CONTENT CONTAINER ===

  // Subtiele schaduw voor container
  pdf.setFillColor(0, 0, 0);
  pdf.setGState(new pdf.GState({ opacity: 0.05 }));
  pdf.roundedRect(16, 67, 180, 200, 3, 3, 'F');
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Witte content area
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(15, 66, 180, 200, 3, 3, 'F');

  // Border
  pdf.setDrawColor(78, 84, 200);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(15, 66, 180, 200, 3, 3);

  // === TEKST RENDERING ===

  let yPosition = 80;
  const leftMargin = 25;
  const contentWidth = 160;
  const lineHeight = 6.5;
  const sectionSpacing = 16;

  const sections = parseVacancyToSections(cleanedVacature);

  Object.entries(sections).forEach(([sectionTitle, content], index) => {
    // Nieuwe pagina check
    if (yPosition > 240) {
      pdf.addPage();

      pdf.setFillColor(0, 0, 0);
      pdf.setGState(new pdf.GState({ opacity: 0.05 }));
      pdf.roundedRect(16, 26, 180, 240, 3, 3, 'F');
      pdf.setGState(new pdf.GState({ opacity: 1 }));

      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(15, 25, 180, 240, 3, 3, 'F');
      pdf.setDrawColor(78, 84, 200);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(15, 25, 180, 240, 3, 3);

      yPosition = 40;
    }

    // Sectie headers
    if (sectionTitle !== 'intro') {
      // Schaduw voor header
      pdf.setFillColor(0, 0, 0);
      pdf.setGState(new pdf.GState({ opacity: 0.1 }));
      pdf.roundedRect(leftMargin - 2, yPosition - 5, contentWidth + 4, 12, 2, 2, 'F');
      pdf.setGState(new pdf.GState({ opacity: 1 }));

      // Header achtergrond
      pdf.setFillColor(78, 84, 200);
      pdf.roundedRect(leftMargin - 3, yPosition - 6, contentWidth + 6, 12, 2, 2, 'F');

      // Header tekst
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.text(sectionTitle.toUpperCase(), leftMargin, yPosition + 2);

      yPosition += sectionSpacing;
    }

    // Content styling
    pdf.setTextColor(45, 55, 72);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);

    const paragraphs = content.split('\n').filter(p => p.trim());

    paragraphs.forEach((paragraph, pIndex) => {
      const trimmed = paragraph.trim();
      if (!trimmed) return;

      if (trimmed.startsWith('\u2022') || trimmed.startsWith('-')) {
        // Bullet points
        const bulletText = trimmed.replace(/^[\u2022\-]\s*/, '');

        pdf.setFillColor(102, 217, 232);
        pdf.circle(leftMargin + 4, yPosition - 1, 2, 'F');

        pdf.setFillColor(255, 255, 255);
        pdf.circle(leftMargin + 4, yPosition - 1, 1, 'F');

        const lines = pdf.splitTextToSize(bulletText, contentWidth - 15);
        pdf.text(lines, leftMargin + 10, yPosition);
        yPosition += lines.length * lineHeight + 4;
      } else {
        // Normale paragraaf
        pdf.setFont('helvetica', 'normal');
        const lines = pdf.splitTextToSize(trimmed, contentWidth);
        pdf.text(lines, leftMargin, yPosition);
        yPosition += lines.length * lineHeight + 6;
      }

      if (pIndex < paragraphs.length - 1) {
        yPosition += 3;
      }
    });

    yPosition += sectionSpacing / 2;

    // Scheidingslijn tussen secties
    if (index < Object.entries(sections).length - 1 && yPosition < 240) {
      pdf.setDrawColor(102, 217, 232);
      pdf.setLineWidth(0.5);
      pdf.setGState(new pdf.GState({ opacity: 0.3 }));
      pdf.line(leftMargin, yPosition - 8, leftMargin + 40, yPosition - 8);
      pdf.setGState(new pdf.GState({ opacity: 1 }));
    }
  });

  // === FOOTER ===

  const pageCount = pdf.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);

    pdf.setFillColor(248, 250, 252);
    pdf.rect(0, 277, 210, 20, 'F');

    pdf.setDrawColor(78, 84, 200);
    pdf.setLineWidth(1.5);
    pdf.line(15, 282, 195, 282);

    pdf.setDrawColor(102, 217, 232);
    pdf.setLineWidth(0.5);
    pdf.line(15, 283.5, 195, 283.5);

    pdf.setTextColor(100, 116, 139);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);

    pdf.text('Gegenereerd door Jesse - Digital Recruiter', 20, 290);

    pdf.setFont('helvetica', 'bold');
    pdf.text(`Pagina ${i} van ${pageCount}`, 190, 290, { align: 'right' });

    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(78, 84, 200);
    pdf.text('www.jessegeul.nl', 105, 290, { align: 'center' });
  }

  pdf.save('vacaturetekst.pdf');
  return true;
};

/**
 * Generate a chat transcript PDF with optional vacancy appendix.
 * Renders a branded header, a table of all messages, and appends
 * the vacancy text on a separate page if one was generated.
 *
 * @param {Array} messages - Chat message array ({ who, type, text })
 */
export const generateChatPDF = (messages) => {
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    putOnlyUsedFonts: true,
    compress: true
  });

  // Header
  pdf.setFillColor(78, 84, 200);
  pdf.rect(0, 0, 210, 50, 'F');

  pdf.setFillColor(143, 148, 251);
  pdf.rect(0, 0, 210, 40, 'F');

  pdf.setFillColor(255, 255, 255);
  pdf.circle(30, 25, 10, 'F');
  pdf.setTextColor(78, 84, 200);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text('JG', 26.5, 29);

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text('CHAT TRANSCRIPT', 50, 22);

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Gesprek met Jesse', 50, 30);

  const today = new Date();
  const dateStr = today.toLocaleDateString('nl-NL');
  pdf.setFontSize(10);
  pdf.text(`Gegenereerd op ${dateStr}`, 50, 38);

  // Chat tabel
  const chatRows = messages
    .filter(x => x.who !== 'system' && x.type !== 'typing')
    .map(x => [x.who === 'user' ? 'Gebruiker' : 'Jesse', norm(x.text)]);

  autoTable(pdf, {
    head: [['Spreker', 'Tekst']],
    body: chatRows,
    startY: 60,
    styles: {
      cellWidth: 'wrap',
      cellPadding: 4,
      fontSize: 10,
      overflow: 'linebreak',
      lineWidth: 0.1
    },
    columnStyles: {
      0: { cellWidth: 35, fontStyle: 'bold' },
      1: { cellWidth: 'auto' }
    },
    headStyles: {
      fillColor: [78, 84, 200],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    margin: { left: 15, right: 15 }
  });

  // Voeg vacature toe als die bestaat
  const rawVacature = detectActualVacancyText(messages);
  const cleanedVacature = cleanVacancyText(rawVacature);

  if (cleanedVacature && cleanedVacature.length > 50) {
    pdf.addPage();

    pdf.setFillColor(78, 84, 200);
    pdf.rect(15, 20, 180, 15, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('FINALE VACATURETEKST', 20, 30);

    pdf.setTextColor(45, 55, 72);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    const textLines = pdf.splitTextToSize(cleanedVacature, 170);
    pdf.text(textLines, 20, 45);
  }

  pdf.save('chat_en_vacature_professional.pdf');
};
